import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, BadgeCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";

export default function Blog() {
  const { data: posts, loading } = useContent("/blog-posts");
  const [active, setActive] = useState("All");
  const list = posts || [];
  const [featured, ...rest] = list;
  const categories = ["All", ...Array.from(new Set(rest.map((p) => p.category)))];
  const filtered = active === "All" ? rest : rest.filter((p) => p.category === active);
  return (
    <>
      <SEO
        title="Travel Blog"
        description="Destination spotlights, travel ideas, member stories, and partner features from Travel Club Navigator Group — inspiration for your next exclusive escape."
        path="/blog"
      />

      {/* HERO */}
      <section className="relative flex min-h-[52vh] items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1706461168370-360a6dcc8ce8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Aerial view of a vibrant turquoise lagoon dotted with tropical islands"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <Overline className="text-accent">Travel Blog</Overline>
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Stories to fuel your wanderlust.
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-200">
              Destination spotlights, travel ideas, and real member journeys — plus the
              occasional (clearly-labeled) partner feature.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-5 pt-16 lg:px-8">
        {loading && <PageLoader />}
        {featured && (
        <Reveal>
          <Link
            to={featured.path || `/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group grid overflow-hidden rounded-[2rem] card-3d lg:grid-cols-2"
          >
            <div className="relative min-h-[320px] overflow-hidden">
              <img
                src={featured.image}
                alt={featured.alt}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <div className="flex items-center gap-2">
                <CategoryBadge category={featured.category} sponsored={featured.sponsored} />
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">
                {featured.title}
              </h2>
              <p className="mt-3 leading-relaxed text-slate-600">{featured.excerpt}</p>
              <div className="mt-5 flex items-center gap-3 text-sm text-slate-400">
                <span>{featured.author}</span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> {featured.readTime}
                </span>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-primary">
                Read the story
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        </Reveal>
        )}
      </section>

      {/* GRID */}
      <section className="section-pad mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="mb-10 flex flex-wrap gap-3">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`blog-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setActive(cat)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                active === cat
                  ? "bg-navy text-white"
                  : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100 hover:text-navy"
              }`}
            >
              {cat}
            </button>
          ))}
        </Reveal>
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((post, i) => (
            <Reveal key={post.slug} delay={i * 0.06}>
              <Link
                to={post.path || `/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl card-3d"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.alt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute left-4 top-4">
                    <CategoryBadge category={post.category} sponsored={post.sponsored} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg font-bold text-navy">{post.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-xs text-slate-400">
                    <span>{post.date}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" /> {post.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export const CategoryBadge = ({ category, sponsored }) =>
  sponsored ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">
      <BadgeCheck className="h-3.5 w-3.5" /> {category} · Sponsored
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy backdrop-blur">
      {category}
    </span>
  );
